// Bowl (goal): the balls must lie still in the bowl. Worlds can draw their own (world.goal.bowl / front).
import { defineGoal } from '../core/registry';
import { lg, shadowOn, shadowOff, DEG } from '../core/draw-helpers';
import type { Ctx2D, Gfx, GoalState } from '../core/types';

export interface Bowl extends GoalState { w: number; zone: { x0: number; x1: number; y0: number; y1: number } }

// gfx: the world's own bowl (world.goal.bowl) for the world being drawn (GFX.setWorld)
export function drawBowl(ctx: Ctx2D, g: { x: number; y: number; w: number }, full: number, t: number, gfx: Gfx) {
  const wd = gfx.worldDef(), own = wd && wd.goal && wd.goal.bowl;
  if (own) { own(ctx, g, full, t, gfx); return; }
  const { x, y, w } = g;
  ctx.save();
  shadowOn(ctx, 16, 8);
  ctx.beginPath();
  ctx.moveTo(x - w / 2 - 6, y - 58); ctx.quadraticCurveTo(x - w / 2 + 4, y + 8, x, y + 8); ctx.quadraticCurveTo(x + w / 2 - 4, y + 8, x + w / 2 + 6, y - 58);
  ctx.closePath();
  ctx.fillStyle = lg(ctx, y - 60, y + 8, '#FFFFFF', '#B9D7FF'); ctx.fill(); shadowOff(ctx);
  // pattern
  ctx.strokeStyle = '#5AA9FF'; ctx.lineWidth = 4;
  ctx.beginPath(); ctx.moveTo(x - w / 2 + 6, y - 34); ctx.quadraticCurveTo(x, y - 20, x + w / 2 - 6, y - 34); ctx.stroke();
  ctx.fillStyle = '#5AA9FF';
  for (let i = -2; i <= 2; i++) { ctx.beginPath(); ctx.arc(x + i * w / 6, y - 16 + Math.abs(i) * -3, 4, 0, 7); ctx.fill(); }
  // porridge rim
  ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 9, 0, 0, 7); ctx.fillStyle = '#F2E2C4'; ctx.fill();
  ctx.beginPath(); ctx.ellipse(x, y - 57, w / 2 - 2, 6, 0, 0, 7); ctx.fillStyle = '#FFF4DC'; ctx.fill();
  // steam
  ctx.strokeStyle = 'rgba(255,255,255,.8)'; ctx.lineWidth = 4; ctx.lineCap = 'round';
  for (let i = -1; i <= 1; i++) {
    ctx.beginPath(); const sx = x + i * 26;
    for (let k = 0; k < 6; k++) ctx.lineTo(sx + Math.sin(t * 0.05 + k + i) * 5, y - 70 - k * 7);
    ctx.stroke();
  }
  ctx.restore();
}
export function drawBowlFront(ctx: Ctx2D, g: { x: number; y: number; w: number }, gfx: Gfx) {
  const wd = gfx.worldDef();
  if (wd && wd.goal && wd.goal.bowl) { if (wd.goal.front) wd.goal.front(ctx, g, gfx); return; }
  const { x, y, w } = g;
  ctx.save(); ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 9, 0, 0, Math.PI); ctx.lineWidth = 5; ctx.strokeStyle = '#FFFFFF'; ctx.stroke(); ctx.restore();
}

export const bowl = defineGoal({
  create(api, g) {
    const { R, add } = api, w = g.w || 130, x = g.x, y = g.y;
    const parts = [
      R(x, y, w, 14, { isStatic: true, chamfer: { radius: 6 } }),
      R(x - w / 2 + 4, y - 26, 12, 58, { isStatic: true, angle: -12 * DEG, chamfer: { radius: 5 } }),
      R(x + w / 2 - 4, y - 26, 12, 58, { isStatic: true, angle: 12 * DEG, chamfer: { radius: 5 } }),
    ];
    parts.forEach(p => { p.kind = 'bowlpart'; p.friction = 0.2; p.restitution = 0.05; add(p); });
    const goal: Bowl = { type: 'bowl', x, y, w, zone: { x0: x - w / 2, x1: x + w / 2, y0: y - 60, y1: y } };
    return goal;
  },
  // wins when all balls have lain still in the bowl for a while
  check(api, goal) {
    const S = api.S, z = (goal as Bowl).zone;
    let n = 0;
    for (const h of S.heroes) if (h.position.x > z.x0 && h.position.x < z.x1 && h.position.y > z.y0 && h.position.y < z.y1 && h.speed < 4) n++;
    if (n > S.inGoal) S.events.push({ e: 'plop', x: goal.x, y: goal.y - 20 });
    S.inGoal = n;
    if (n >= S.need) { S.winT++; if (S.winT > 25) { S.won = true; S.events.push({ e: 'win' }); } } else S.winT = 0;
  },
  blockers(g) { const out = [], w = g.w || 130; for (let i = -2; i <= 2; i++) out.push({ x: g.x + i * w / 5, y: g.y - 28, r: 34 }); return out; },
  draw: (ctx, goal, inGoal, t, gfx) => drawBowl(ctx, goal as Bowl, inGoal, t, gfx),
  drawFront: (ctx, goal, gfx) => drawBowlFront(ctx, goal as Bowl, gfx),
  // hearts above the bowl
  onWin(goal, f) {
    for (let i = 0; i < 10; i++) f.particle({ x: goal.x + (Math.random() - .5) * 80, y: goal.y - 70, vx: (Math.random() - .5) * 3, vy: -3 - Math.random() * 3, life: 70, max: 70, col: '#FF5A8A', r: 5 + Math.random() * 4, kind: 'heart' });
  },
  // in the porridge: the ball has dropped down into the bowl and cannot be seen
  hideBall(goal, b, fx) {
    const z = (goal as Bowl).zone;
    if (b.position.x > z.x0 && b.position.x < z.x1 && b.position.y > z.y0 + 6 && b.position.y < z.y1 + 10) { if (!b.inBowl) { b.inBowl = true; fx.burst(b.position.x, z.y0 + 4, '#FFF4DC', 10, 2.5); } return true; }
    return false;
  },
});
